import React from "react";
import {
  BankAccount,
  Card,
  getCardNetworkByShortCode,
  translateBankAccountType,
} from "constants/FundingOption";
import {
  COMMON_NAMESPACE,
  MANAGE_PAYMENT_METHODS_MODAL,
  BALANCE_NAMESPACE,
} from "constants/i18n-namespaces";
import useWallet from "hooks/useWallet";
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import Sheet from "react-modal-sheet";
import XButton from "assets/icons/x_button.svg";
import { CreditCardLogo, PaymentMethodRow } from "kubra-ux-forge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBank } from "@fortawesome/pro-solid-svg-icons";
import _ from "lodash";
import { CardType } from "kubra-ux-forge/dist/foundation/CreditCardLogos/CreditCardLogo";
import { isExpiryCardDateValid } from "../credit-debit-modal";
import IamApi from "api/IamApi";
import { useDispatch, useSelector } from "react-redux";
import { getBiller } from "selectors";
import { customStyles } from "constants/customStyles";
import { getIsMobile } from "selectors/appState";
import { getWallet } from "selectors/paymentIntent";
import { ACTION } from "actions/paymentIntentActions";
import { KUBRA_TERMS_OF_USE_URL } from "constants/externalUrls";
import { useToast } from "context/ToastContext";

export interface IManagePaymentMethodsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isPayable?: boolean;
}

export const ManagePaymentMethodsModal = (
  props: IManagePaymentMethodsModalProps,
) => {
  const { isOpen, onClose, isPayable = false } = props;
  const { t } = useTranslation([
    MANAGE_PAYMENT_METHODS_MODAL,
    BALANCE_NAMESPACE,
  ]);
  const isMobile = useSelector(getIsMobile);
  const { getWalletItems, loadWalletItems } = useWallet(isPayable);
  const biller = useSelector(getBiller);
  const walletItems = getWalletItems();
  const bankAccounts = walletItems.filter(
    (_) => _.fundingOption === BankAccount,
  );
  const cards = walletItems.filter((_) => _.fundingOption === Card);
  const wallet = useSelector(getWallet);
  const dispatch = useDispatch();
  const { addToast } = useToast();

  const deleteWalletToast = (fundingOption: string) => {
    let message = "";
    if (fundingOption === BankAccount) {
      message = t("bank-deleted-toast", { ns: BALANCE_NAMESPACE });
    } else if (fundingOption === Card) {
      message = t("card-deleted-toast", { ns: BALANCE_NAMESPACE });
    }
    if (!message) {
      return;
    }
    addToast(message, "success");
  };

  const deleteWallet = async (
    clientId: string,
    fundingSourceId: string,
    accountV2: string,
  ) => {
    if (isPayable) {
      const deletePayableWallet = await IamApi.ezpay.deletePayableWalletItem(
        clientId,
        fundingSourceId,
      );
      if (deletePayableWallet.status === 200) {
        const walletItemToDelete = walletItems?.find(
          (_) => _.fundingSourceId === fundingSourceId,
        );
        if (
          walletItemToDelete?.fundingOption === BankAccount ||
          walletItemToDelete?.fundingOption === Card
        ) {
          deleteWalletToast(walletItemToDelete.fundingOption);
        }
      }
    } else {
      !accountV2 || accountV2?.toLowerCase() === "false"
        ? await IamApi.ezpay.deleteWalletItem(clientId, fundingSourceId)
        : await IamApi.ezpay.deleteWalletItemV2(clientId, fundingSourceId);
    }

    // TODO: confirm if we should clear deleted funding source from the Redux store?
    dispatch({ type: "DELETE_WALLET_ITEM", payload: fundingSourceId });
    dispatch({ type: ACTION.REMOVE_WALLET });

    // Refresh wallet state after 2s to avoid interuptting delete animation
    setTimeout(() => {
      loadWalletItems(clientId, biller.featureFlags.accountV2);
    }, 2000);
  };

  if (isMobile) {
    return (
      <>
        <Sheet
          isOpen={isOpen}
          onClose={onClose}
          detent="content-height"
          disableDrag={true}
          aria-labelledby="select-payment-methods-modal-title-mobile"
        >
          <Sheet.Container>
            <Sheet.Header>
              <div className="react-modal-sheet-header">
                <div
                  id="select-payment-methods-modal-title-mobile"
                  className="help-modal-header-text"
                >
                  {t("select-method")}
                </div>
                <button
                  className="modal-close"
                  onClick={onClose}
                  aria-label={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
                  data-cy="modal-close-x-help"
                >
                  <img
                    src={XButton}
                    alt={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
                    className="modal-close-x"
                  />
                </button>
              </div>
            </Sheet.Header>
            <Sheet.Content>
              <div className="wallet-frame">
                {bankAccounts.map((bankAccount) => {
                  return (
                    <PaymentMethodRow
                      key={bankAccount.fundingSourceId}
                      icon={<FontAwesomeIcon icon={faBank}></FontAwesomeIcon>}
                      label={`${translateBankAccountType(
                        bankAccount.presentableMOPCode,
                        t,
                      )} ${bankAccount.maskedNumber}`}
                      onDelete={() =>
                        deleteWallet(
                          biller.clientId,
                          bankAccount.fundingSourceId,
                          biller.featureFlags.accountV2,
                        )
                      }
                      /* @ts-ignore */
                      className="payment-method-row"
                      variant="default"
                      /* @ts-ignore */
                      dataCy={`payment-mehod-row-${bankAccount.presentableMOPCode} ${bankAccount.maskedNumber}`}
                    />
                  );
                })}
                {cards.map((card) => {
                  return (
                    <PaymentMethodRow
                      key={card.fundingSourceId}
                      error={!isExpiryCardDateValid(card.expiry)}
                      expiry={card.expiry}
                      icon={
                        <CreditCardLogo
                          cardType={
                            _.lowerCase(
                              getCardNetworkByShortCode(
                                card.presentableMOPCode,
                              ),
                            ) as CardType
                          }
                        />
                      }
                      label={`${getCardNetworkByShortCode(
                        card.presentableMOPCode,
                      )} ${card.maskedNumber}`}
                      onDelete={() =>
                        deleteWallet(
                          biller.clientId,
                          card.fundingSourceId,
                          biller.featureFlags.accountV2,
                        )
                      }
                      /* @ts-ignore */
                      className="payment-method-row"
                      variant="default"
                      /* @ts-ignore */
                      dataCy={`payment-mehod-row-${getCardNetworkByShortCode(
                        card.presentableMOPCode,
                      )} ${card.maskedNumber}`}
                    />
                  );
                })}
                <div className="wallet-info-credential">
                  <div>
                    {t("store-wallet-credential")}
                    <button
                      className="link-button"
                      data-testid="button-terms-of-use"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open(
                          KUBRA_TERMS_OF_USE_URL,
                          "_blank",
                          "noopener",
                        );
                      }}
                    >
                      {t("terms-of-use")}
                    </button>
                    {t("store-wallet-credential-continue")}
                  </div>
                </div>
              </div>
            </Sheet.Content>
          </Sheet.Container>
          <Sheet.Backdrop />
        </Sheet>
      </>
    );
  }

  return (
    <>
      {/* @ts-ignore */}
      <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        className="modal-frame-help"
        shouldCloseOnOverlayClick={false}
        onRequestClose={onClose}
        style={customStyles}
        aria={{
          labelledby: "select-payment-methods-modal-title",
        }}
      >
        <div className="modal-header">
          <div
            id="select-payment-methods-modal-title"
            className="help-modal-header-text"
          >
            {t("select-method")}
          </div>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
            data-cy="modal-close-x-help"
          >
            <img
              src={XButton}
              alt={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
              className="modal-close-x"
            />
          </button>
        </div>
        <div className="wallet-frame">
          {bankAccounts.map((bankAccount) => {
            return (
              <PaymentMethodRow
                key={bankAccount.fundingSourceId}
                icon={<FontAwesomeIcon icon={faBank}></FontAwesomeIcon>}
                label={`${translateBankAccountType(
                  bankAccount.presentableMOPCode,
                  t,
                )} ${bankAccount.maskedNumber}`}
                onDelete={() =>
                  deleteWallet(
                    biller.clientId,
                    bankAccount.fundingSourceId,
                    biller.featureFlags.accountV2,
                  )
                }
                /* @ts-ignore */
                className="payment-method-row"
                variant="default"
                /* @ts-ignore */
                dataCy={`payment-mehod-row-${bankAccount.presentableMOPCode} ${bankAccount.maskedNumber}`}
              />
            );
          })}
          {cards.map((card) => {
            return (
              <PaymentMethodRow
                key={card.fundingSourceId}
                error={!isExpiryCardDateValid(card.expiry)}
                expiry={card.expiry}
                icon={
                  <CreditCardLogo
                    cardType={
                      _.lowerCase(
                        getCardNetworkByShortCode(card.presentableMOPCode),
                      ) as CardType
                    }
                  />
                }
                label={`${getCardNetworkByShortCode(card.presentableMOPCode)} ${
                  card.maskedNumber
                }`}
                onDelete={() =>
                  deleteWallet(
                    biller.clientId,
                    card.fundingSourceId,
                    biller.featureFlags.accountV2,
                  )
                }
                /* @ts-ignore */
                className="payment-method-row"
                variant="default"
                /* @ts-ignore */
                dataCy={`payment-mehod-row-${getCardNetworkByShortCode(
                  card.presentableMOPCode,
                )} ${card.maskedNumber}`}
              />
            );
          })}
          <div className="wallet-info-credential">
            <div>
              {t("store-wallet-credential")}
              <button
                data-testid="terms-link-button"
                className="link-button"
                data-cy="button-terms-of-use"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(KUBRA_TERMS_OF_USE_URL, "_blank", "noopener");
                }}
              >
                {t("terms-of-use")}
              </button>
              {t("store-wallet-credential-continue")}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
